// Painel "Notificações no celular" (B29-M5): o mesmo para o CMS (Ponte) e para o item Mais do
// celular. O aparelho (permissão, inscrição) fica em pwa.ts; aqui, só o que aparece na tela.
// O texto que vem do servidor (nome do aparelho, data) entra por textContent, nunca por HTML.

import { atualizaQuaisPush, cancelarPush, ehIos, inscreverPush, inscritoPush, pushSuportado, type QuaisPush } from './pwa.js';

/** Os textos da tela: o caller (escritório ou CMS) traduz e entrega. */
export interface TextosPush {
  ativar: string;
  desativar: string;
  testar: string;
  testeOk: string;
  testeSemAparelho: string;
  testeFalhou: string;
  ativo: string;
  inativo: string;
  semSuporte: string;
  ios: string;
  negada: string;
  falhou: string;
  quando: string;
  precisa: string;
  concluiu: string;
  aparelhos: string;
  nenhum: string;
  remover: string;
  este: string;
  salvo: string;
}

interface AparelhoLista {
  id: string;
  ua: string;
  criadoEm: number;
  quais: QuaisPush;
}

const CHAVE_QUAIS = 'agzos.push.quais';
const PADRAO_QUAIS: QuaisPush = { precisa: true, concluiu: false };

/** Preferências deste aparelho (ficam no localStorage até a inscrição existir). */
function quaisSalvos(): QuaisPush {
  try {
    const raw = JSON.parse(localStorage.getItem(CHAVE_QUAIS) ?? 'null') as Partial<QuaisPush> | null;
    if (raw && typeof raw.precisa === 'boolean' && typeof raw.concluiu === 'boolean') return { precisa: raw.precisa, concluiu: raw.concluiu };
  } catch {
    /* sem localStorage: volta ao padrão */
  }
  return { ...PADRAO_QUAIS };
}

function guardaQuais(quais: QuaisPush): void {
  try {
    localStorage.setItem(CHAVE_QUAIS, JSON.stringify(quais));
  } catch {
    /* sem localStorage: as escolhas valem só nesta tela */
  }
}

/** O id que o servidor dá a este aparelho: os 16 primeiros hex do SHA-256 do endpoint (ver src/pushstore.ts). */
async function idDeste(): Promise<string | undefined> {
  if (!pushSuportado()) return undefined;
  const sub = await (await navigator.serviceWorker.ready).pushManager.getSubscription();
  if (!sub) return undefined;
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(sub.endpoint));
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('').slice(0, 16);
}

/** iPhone fora do app instalado: o Safari não entrega push, então a tela explica. */
function ehIosSemInstalar(): boolean {
  const instalado = matchMedia('(display-mode: standalone)').matches || (navigator as unknown as { standalone?: boolean }).standalone === true;
  return ehIos(navigator.userAgent, navigator.maxTouchPoints) && !instalado;
}

function el<K extends keyof HTMLElementTagNameMap>(tag: K, classe?: string, texto?: string): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (classe) e.className = classe;
  if (texto !== undefined) e.textContent = texto;
  return e;
}

/** Monta o painel dentro de `host` (apaga o que estava lá). Pode ser chamado de novo para atualizar. */
export async function montaPainelPush(host: HTMLElement, textos: TextosPush): Promise<void> {
  host.replaceChildren();
  const quais = quaisSalvos();

  const estado = el('p', 'dica push-estado');
  const aviso = el('p', 'aviso push-aviso', textos.ios);
  aviso.hidden = true;
  const msg = el('p', 'dica push-msg');
  msg.setAttribute('role', 'status');

  const botoes = el('div', 'push-acoes');
  const btnAtivar = el('button', 'push-ativar', textos.ativar);
  const btnDesativar = el('button', 'push-desativar', textos.desativar);
  const btnTestar = el('button', 'push-testar', textos.testar);
  botoes.append(btnAtivar, btnDesativar, btnTestar);

  const escolhas = el('fieldset', 'push-quais');
  escolhas.append(el('legend', '', textos.quando));
  const caixa = (rotulo: string, marcada: boolean): HTMLInputElement => {
    const input = el('input');
    input.type = 'checkbox';
    input.checked = marcada;
    const label = el('label', 'interruptor');
    label.append(input, ` ${rotulo}`);
    escolhas.append(label);
    return input;
  };
  const caixaPrecisa = caixa(textos.precisa, quais.precisa);
  const caixaConcluiu = caixa(textos.concluiu, quais.concluiu);
  const lePreferencias = (): QuaisPush => ({ precisa: caixaPrecisa.checked, concluiu: caixaConcluiu.checked });

  const titulo = el('h4', 'push-titulo', textos.aparelhos);
  const lista = el('ul', 'push-lista');
  host.append(estado, aviso, botoes, escolhas, msg, titulo, lista);

  const mostra = (texto: string, erro = false): void => {
    msg.textContent = texto;
    msg.classList.toggle('erro', erro);
  };

  async function pintaLista(): Promise<void> {
    const res = await fetch('/api/push/inscricoes', { cache: 'no-store' }).catch(() => undefined);
    const dados = res?.ok ? ((await res.json()) as { aparelhos?: AparelhoLista[] }) : undefined;
    const aparelhos = dados?.aparelhos ?? [];
    const eleDeste = await idDeste();
    lista.replaceChildren();
    if (!aparelhos.length) lista.append(el('li', 'dica', textos.nenhum));
    for (const a of aparelhos) {
      const item = el('li', 'push-item');
      const data = new Date(a.criadoEm).toLocaleDateString();
      item.append(el('span', '', `${a.ua} · ${data}${a.id === eleDeste ? ` (${textos.este})` : ''}`));
      const quaisTxt = [a.quais.precisa ? textos.precisa : '', a.quais.concluiu ? textos.concluiu : ''].filter(Boolean).join(' · ');
      if (quaisTxt) item.append(el('small', 'dica', quaisTxt));
      const remove = el('button', 'push-remover', textos.remover);
      remove.addEventListener('click', async () => {
        const r = await fetch('/api/push/cancelar', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: a.id }) });
        if (r.ok && a.id === eleDeste) await cancelarPush();
        await pinta();
      });
      item.append(remove);
      lista.append(item);
    }
  }

  async function pinta(): Promise<void> {
    const inscrito = await inscritoPush();
    estado.textContent = !pushSuportado() ? textos.semSuporte : inscrito ? textos.ativo : textos.inativo;
    btnAtivar.hidden = inscrito;
    btnDesativar.hidden = !inscrito;
    aviso.hidden = !ehIosSemInstalar();
    await pintaLista();
  }

  btnAtivar.addEventListener('click', async () => {
    const quaisAtuais = lePreferencias();
    guardaQuais(quaisAtuais);
    const r = await inscreverPush(quaisAtuais);
    if (r.ok) mostra(textos.salvo);
    else mostra(r.erro === 'negada' ? textos.negada : r.erro === 'sem-suporte' ? textos.semSuporte : textos.falhou, true);
    await pinta();
  });

  btnDesativar.addEventListener('click', async () => {
    const ok = await cancelarPush();
    mostra(ok ? textos.inativo : textos.falhou, !ok);
    await pinta();
  });

  btnTestar.addEventListener('click', async () => {
    const res = await fetch('/api/push/testar', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' });
    const dados = (await res.json().catch(() => ({}))) as { enviadas?: number };
    if (res.status === 400) mostra(textos.testeSemAparelho, true);
    else if (res.ok && (dados.enviadas ?? 0) > 0) mostra(textos.testeOk);
    else mostra(textos.testeFalhou, true);
  });

  for (const input of [caixaPrecisa, caixaConcluiu]) {
    input.addEventListener('change', async () => {
      const atual = lePreferencias();
      guardaQuais(atual);
      if (await inscritoPush()) {
        const ok = await atualizaQuaisPush(atual);
        mostra(ok ? textos.salvo : textos.falhou, !ok);
      }
    });
  }

  await pinta();
}
